/* eslint-disable max-lines */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  NewRegistrationData,
  usePaymentRegistration
} from '@utils/api/organizations/use-register-with-payment.tsx';
import toast from 'react-hot-toast';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import { PATHS } from '../../router/paths.ts';
import Loader from '@components/atoms/loaders/loader.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import {
  Check,
  Globe,
  Languages,
  Layers,
  Newspaper,
  UsersIcon,
  ArrowRight
} from 'lucide-react';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useOrgCurrency } from '@utils/hooks/use-org-currency';
import { formatCurrency } from '@utils/helpers/currency';
import { Card } from '@components/atoms/ui-kit/card.tsx';
import { Heading, Text } from '@components/atoms/ui-kit/text.tsx';

/* eslint-disable no-restricted-syntax -- Semantic plan-tier colors (bronze/silver/gold/platinum) and validation states must stay stable across themes */

type FormValues = {
  product: string;
};

const SubscriptionPlans = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);

  const themeContext = useThemeContext();
  const theme = themeContext?.theme ?? 'light';
  const getColour = themeContext?.getColour;
  const isDark = theme === 'dark';

  const currency = useOrgCurrency();

  const tiers = [
    {
      name: 'Bronze',
      id: 'STARTER:2024',
      product: 'STARTER',
      tier: 'Starter',
      priceMonthly: formatCurrency(119, currency),
      description: 'Small teams and local organizations',
      features: ['Batch speech to text', 'UI & API access', '3 seats'],
      color: 'border-amber-500',
      textColor: 'text-[#8C4A1F]',
      chipStyle: {
        light: { bg: '#FEF3E7', text: '#8C4A1F', border: '#FCD3A7' },
        dark: {
          bg: 'rgba(251, 191, 36, 0.15)',
          text: '#FCD34D',
          border: 'rgba(251, 191, 36, 0.3)'
        }
      },
      featured: false
    },
    {
      name: 'Silver',
      id: 'PROFESSIONAL:2024',
      product: 'PROFESSIONAL',
      tier: 'Professional',
      priceMonthly: formatCurrency(479, currency),
      description: 'Mid sized organizations',
      features: [
        'Batch & realtime speech to text',
        'UI & API access',
        '10 seats',
        'Machine Translation',
        'Summarization',
        'Automatic Meeting Minutes',
        'Priority processing'
      ],
      color: 'border-slate-500',
      textColor: 'text-[#4B5563]',
      chipStyle: {
        light: { bg: '#F5F5F7', text: '#4B5563', border: '#D4D4D8' },
        dark: {
          bg: 'rgba(148, 163, 184, 0.15)',
          text: '#CBD5E1',
          border: 'rgba(148, 163, 184, 0.3)'
        }
      },
      featured: false
    },
    {
      name: 'Gold',
      id: 'BUSINESS:2024',
      product: 'BUSINESS',
      tier: 'Business',
      priceMonthly: formatCurrency(1199, currency),
      description: 'Large organizations',
      features: [
        'Batch & Realtime speech to text',
        'UI & API access',
        'Unlimited seats',
        'Machine Translation',
        'Summarization',
        'Automatic Meeting Minutes',
        'Priority Processing',
        'Dedicated SLA',
        'Roadmap access',
        'Custom models and translation glossaries'
      ],
      color: 'border-yellow-500',
      textColor: 'text-[#B45309]',
      chipStyle: {
        light: { bg: '#FEF9C3', text: '#B45309', border: '#FACC15' },
        dark: {
          bg: 'rgba(250, 204, 21, 0.15)',
          text: '#FDE047',
          border: 'rgba(250, 204, 21, 0.3)'
        }
      },
      featured: true
    },
    {
      name: 'Platinum',
      id: 'ENTERPRISE:2024',
      product: 'ENTERPRISE',
      tier: 'Enterprise',
      priceMonthly: formatCurrency(10000, currency),
      description: 'Tailored for Enterprises',
      features: [
        'All services from Bronze to Gold',
        'Local deployment possibilities',
        'Feature development on request',
        'Integration support',
        'Tailored high volume fair use',
        'Custom SLA agreement'
      ],
      color: 'border-slate-400',
      textColor: 'text-[#334155]',
      chipStyle: {
        light: { bg: '#F3F4F6', text: '#334155', border: '#E5E7EB' },
        dark: {
          bg: 'rgba(100, 116, 139, 0.15)',
          text: '#94A3B8',
          border: 'rgba(100, 116, 139, 0.3)'
        }
      },
      featured: false,
      isEnterprise: true
    }
  ];

  const { data: organization, isLoading: organizationIsLoading } =
    useOrganization();
  const { mutate } = usePaymentRegistration();

  const features = [
    {
      name: t('unlimited-number-of-users'),
      description: '',
      icon: UsersIcon
    },
    {
      name: t('37-plus-languages'),
      description: '',
      icon: Globe
    },
    {
      name: t('machine-translation'),
      description: '',
      icon: Languages
    },
    {
      name: t('speech-to-text-processing'),
      description: '',
      icon: Newspaper
    },
    {
      name: t('ui-&-api-access'),
      description: '',
      icon: Layers
    }
  ];

  const steps = [
    { id: '01', name: t('select-subscription'), href: '#', status: 'current' },
    { id: '02', name: t('payment-details'), href: '#', status: 'upcoming' }
  ];

  const validationSchema = Yup.object({
    product: Yup.string().required(t('product-required'))
  });

  const { handleSubmit, setValue } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      product: ''
    }
  });

  const handleNextStepToForm = (productCode: string) => {
    setValue('product', productCode, { shouldValidate: true });

    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onSubmit: SubmitHandler<FormValues> = values => {
    const payload: NewRegistrationData = {
      product: values.product
    };

    mutate(payload, {
      onSuccess: data => {
        if (data?.result?.mollie_checkout_url) {
          toast.success(
            t(
              'plan-submitted-successfully-you-will-be-redirected-to-mollie-payment-page'
            )
          );
          setTimeout(() => {
            window.location.href = data.result.mollie_checkout_url;
          }, 3000);
        } else {
          toast.error(t('plan-submitting-failed'));
        }
      },
      onError: error => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const detail: any = error?.response?.data?.detail;
        const message =
          (Array.isArray(detail) && detail[0]?.msg) ||
          error?.response?.data?.error_description ||
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          t('plan-submitting-failed');

        toast.error(message);
      }
    });
  };

  useEffect(() => {
    if (organization?.is_resellee) {
      navigate(PATHS().HOME);
    }
  }, [organization, navigate]);

  if (organizationIsLoading || organization?.is_resellee) {
    return (
      <div className="h-screen w-full flex justify-center items-center">
        <Loader height={10} width={3} />
      </div>
    );
  }

  const cardBorderColor = getColour ? getColour('border') : '#d4d4d8';
  const cardBgColor = getColour ? getColour('firstLayer') : '#ffffff';
  const cardHoverBorderColor = getColour
    ? getColour('borderPrimary')
    : '#0b57d0';

  return (
    <div className="bg-app min-h-screen px-3 md:px-8 py-6 md:py-10">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Simple header */}
        <div className="text-center space-y-2">
          <Heading level={1} size="3xl">
            {t('subscription-plans')}
          </Heading>
          <Text as="p" size="sm" tone="muted" className="max-w-xl mx-auto">
            {t('select-subscription')}
          </Text>
        </div>

        {/* All plans include */}
        <Card
          padding="none"
          className="px-4 py-4 md:px-6 md:py-5"
          style={{
            backgroundColor: getColour ? getColour('firstLayer') : undefined
          }}
        >
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            <div className="flex items-center gap-2">
              <span
                className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#0b57d0]/10 text-[#0b57d0]"
                style={
                  isDark
                    ? {
                        backgroundColor: 'rgba(59,130,246,0.15)',
                        color: 'rgb(96 165 250)'
                      }
                    : undefined
                }
              >
                <Check className="h-4 w-4" />
              </span>
              <Text as="span" size="sm" weight="semibold">
                {t('all-plans-include')}:
              </Text>
            </div>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3">
              {features.map(feature => (
                <div
                  key={feature.name}
                  className="flex items-center gap-1.5 rounded-full border px-3 py-1"
                  style={{
                    backgroundColor: getColour
                      ? getColour('baseLayer')
                      : '#f4f4f5',
                    borderColor: getColour ? getColour('border') : '#d4d4d8'
                  }}
                >
                  <feature.icon
                    className="h-3.5 w-3.5 text-[#0b57d0]"
                    style={isDark ? { color: 'rgb(129 140 248)' } : undefined}
                  />
                  <Text as="span" size="xs">
                    {feature.name}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Stepper */}
        <Card
          padding="none"
          className="px-4 py-3 md:px-6 md:py-4"
          style={{
            backgroundColor: getColour ? getColour('firstLayer') : undefined
          }}
        >
          <nav aria-label="Progress">
            <ol className="flex items-center justify-center gap-4">
              {steps.map((step, idx) => {
                const stepNumber = idx + 1;
                const isCurrent = currentStep === stepNumber;
                const isComplete = currentStep > stepNumber;

                const baseContainerStyle = {
                  borderColor: getColour ? getColour('border') : '#d4d4d8',
                  backgroundColor: getColour
                    ? getColour('baseLayer')
                    : '#f4f4f5',
                  color: getColour ? getColour('inactiveText') : '#595960'
                };

                const currentStyle = isDark
                  ? {
                      borderColor: 'rgb(96 165 250)',
                      backgroundColor: 'rgba(37,99,235,0.12)',
                      color: 'rgb(191 219 254)'
                    }
                  : undefined;

                const completeStyle = isDark
                  ? {
                      borderColor: 'rgb(16 185 129)',
                      backgroundColor: 'rgba(16,185,129,0.12)',
                      color: 'rgb(110 231 183)'
                    }
                  : undefined;

                return (
                  <li key={step.name} className="flex items-center gap-2">
                    <div
                      className="flex items-center gap-2 px-3 py-1.5 rounded-full border"
                      style={{
                        ...baseContainerStyle,
                        ...(isCurrent ? currentStyle : {}),
                        ...(isComplete ? completeStyle : {})
                      }}
                    >
                      <span
                        className="flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold"
                        style={
                          isCurrent
                            ? { backgroundColor: '#0b57d0', color: '#fff' }
                            : isComplete
                              ? {
                                  backgroundColor: 'rgb(16 185 129)',
                                  color: '#fff'
                                }
                              : {
                                  backgroundColor: getColour
                                    ? getColour('firstLayer')
                                    : '#ffffff',
                                  color: getColour
                                    ? getColour('normalText')
                                    : '#28313e'
                                }
                        }
                      >
                        {isComplete ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : (
                          step.id
                        )}
                      </span>
                      <Text as="span" size="sm">
                        {step.name}
                      </Text>
                    </div>

                    {idx < steps.length - 1 && (
                      <div
                        className="hidden md:block h-px w-10"
                        style={{
                          backgroundColor: getColour
                            ? getColour('border')
                            : '#d4d4d8'
                        }}
                      />
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>
        </Card>

        {/* Step 1 — plan selection */}
        {currentStep === 1 && (
          <div className="mt-4">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
              {tiers.map(tier => (
                <div
                  key={tier.id}
                  className="group relative flex flex-col rounded-2xl border shadow-sm p-4 transition-all hover:-translate-y-1 hover:shadow-lg"
                  style={{
                    backgroundColor: cardBgColor,
                    borderColor: tier.featured
                      ? cardHoverBorderColor
                      : cardBorderColor
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = cardHoverBorderColor;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = tier.featured
                      ? cardHoverBorderColor
                      : cardBorderColor;
                  }}
                >
                  {tier.featured && (
                    <span className="absolute -top-3 left-4 inline-flex items-center rounded-full bg-yellow-500 px-2.5 py-1 text-[11px] font-medium text-zinc-900 shadow-sm">
                      {t('most-popular')}
                    </span>
                  )}

                  {/* Header row: chips + price */}
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex flex-col gap-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {/* Name chip — semantic tier color, exempted */}
                        <span
                          className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium border"
                          style={{
                            backgroundColor: isDark
                              ? tier.chipStyle.dark.bg
                              : tier.chipStyle.light.bg,
                            color: isDark
                              ? tier.chipStyle.dark.text
                              : tier.chipStyle.light.text,
                            borderColor: isDark
                              ? tier.chipStyle.dark.border
                              : tier.chipStyle.light.border
                          }}
                        >
                          {tier.name}
                        </span>

                        {/* Tier type chip */}
                        <Text
                          as="span"
                          size="xs"
                          weight="medium"
                          tone="muted"
                          className="inline-flex items-center rounded-full px-2 py-0.5 uppercase tracking-[0.14em] border"
                          style={{
                            backgroundColor: getColour
                              ? getColour('baseLayer')
                              : '#f4f4f5',
                            borderColor: cardBorderColor
                          }}
                        >
                          {tier.tier}
                        </Text>
                      </div>
                    </div>

                    <div className="text-right">
                      <Text as="p" size="lg" weight="semibold">
                        {tier.priceMonthly}
                      </Text>
                      <Text as="p" size="xs" tone="muted">
                        / {t('month')}
                      </Text>
                    </div>
                  </div>

                  {/* Description */}
                  <Text as="p" size="xs" tone="muted" className="mb-4">
                    {tier.description}
                  </Text>

                  {/* Features */}
                  <div className="flex-1">
                    <Text
                      as="p"
                      size="xs"
                      weight="semibold"
                      tone="muted"
                      className="mb-2 uppercase tracking-[0.14em]"
                    >
                      {t('services-included')}
                    </Text>
                    <ul className="space-y-1.5">
                      {tier.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="mt-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-[#0b57d0]/10 text-[#0b57d0] flex-shrink-0">
                            <Check className="h-3 w-3" />
                          </span>
                          <Text as="span" size="xs">
                            {feature}
                          </Text>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA */}
                  <div className="mt-4">
                    {tier.isEnterprise ? (
                      <Button
                        label={t('contact-us')}
                        variant="smoke"
                        icon={<ArrowRight className="h-4 w-4" />}
                        iconPosition="right"
                        className="w-full"
                        onClick={() =>
                          (window.location.href =
                            'mailto:frans@scriptix.io?subject=Enterprise%20Plan%20Inquiry&body=Hi%2C%20I%20am%20interested%20in%20the%20Enterprise%20plan.')
                        }
                      />
                    ) : (
                      <Button
                        label={t('get-started')}
                        variant={tier.featured ? 'primary' : 'secondary'}
                        icon={<ArrowRight className="h-4 w-4" />}
                        iconPosition="right"
                        className="w-full"
                        onClick={() => handleNextStepToForm(tier.product)}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2 — payment */}
        {currentStep === 2 && (
          <div className="mt-8 flex justify-center">
            <Card
              padding="lg"
              className="w-full max-w-xl text-center"
              style={{
                backgroundColor: getColour ? getColour('firstLayer') : undefined
              }}
            >
              <p
                className="text-xs font-semibold inline-flex items-center gap-1 rounded-full px-3 py-1 mb-3"
                style={{
                  backgroundColor: isDark ? 'rgba(16,185,129,0.12)' : '#ecfdf5',
                  color: isDark ? 'rgb(110 231 183)' : '#047857'
                }}
              >
                <span
                  className="flex h-4 w-4 items-center justify-center rounded-full"
                  style={{ backgroundColor: '#10b981', color: '#fff' }}
                >
                  <Check className="h-3 w-3" />
                </span>
                {t('secure-payments-powered-by-mollie')}
              </p>
              <Heading level={2} size="lg" className="mb-2">
                {t('payment-details')}
              </Heading>
              <Text as="p" size="sm" tone="muted" className="mb-6">
                {t(
                  'you-will-be-redirected-to-mollie-in-order-to-perform-your-first-payment-once-youve-completed-the-payment-we-will-sent-you-an-e-mail-with-the-activation-details'
                )}
              </Text>

              <div className="flex flex-wrap justify-center gap-3">
                <Button
                  label={t('back')}
                  size="medium"
                  className="w-full sm:w-auto"
                  variant="secondary"
                  onClick={handlePreviousStep}
                />
                <Button
                  label={t('go-to-payment')}
                  size="medium"
                  className="w-full sm:w-auto"
                  type="button"
                  variant="primary"
                  onClick={handleSubmit(onSubmit)}
                />
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPlans;
